import { faEye } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import React, { useContext } from "react";
import { Badge, Card, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import { TrackEFilingStatus } from "../../config/filingConstants";
import { IFilingResponse } from "../../config/interfaces";
import { UtilService } from "../../services";
import { FilingTableHeadings } from "../TableHeaders";
import RowActionOverlay from "../overlay/RowActionOverlay";
import TablePagination from "../pagination/TablePagination";
import NoRecordFound from "../placeholders/NoRecordFound";

type Props = {
  filingsData: IFilingResponse[];
  pageCount: number;
  forcePage: number;
  noRecords: boolean;
  handleOnPageChange: (event: any) => void;
  handleRefresh: () => void;
  hideColumns?: string[];
};

const ITRFilingsTable: React.FC<Props> = (props: Props) => {
  const {
    filingsData,
    pageCount,
    forcePage,
    handleOnPageChange,
    noRecords = false,
    hideColumns
  } = props;

  const navigate = useNavigate();
  const { rulesSvc } = useContext(AppRulesContext);

  return (
    <>
      <Card className="table-card">
        <Card.Body>
          <Table
            hover={
              noRecords ? false : rulesSvc.canAccess(rulesSvc.updateAndSingleEFiling) ? true : true
            }
            responsive
          >
            <thead>
              <tr>
                {FilingTableHeadings.map(
                  (_heading, index) =>
                    !hideColumns?.includes(FilingTableHeadings[index].title) && (
                      <th
                        className={`${_heading.classPrefix}${_heading.size}`}
                        key={`filing-th-${index}`}
                      >
                        {_heading.title}
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {!noRecords ? (
                <>
                  {filingsData.map((filing, index) => (
                    <tr
                      key={`filing-${index}`}
                      onClick={() =>
                        rulesSvc.canAccess(rulesSvc.updateAndSingleEFiling)
                          ? navigate(`/itr-filings/${filing.uuid}`)
                          : null
                      }
                      className={classNames(filing?.completedAt && "bg-warningLight")}
                    >
                      {!hideColumns?.includes(FilingTableHeadings[0].title) && (
                        <td className="text-capitalize">
                          {filing?.name || "-"}
                          {filing?.pan && (
                            <>
                              {filing?.name && ", "}
                              <small className="text-greyDark text-uppercase small-para">
                                {filing.pan}
                              </small>
                            </>
                          )}
                          <div className="badges-wrap">
                            <Badge
                              bg={TrackEFilingStatus[filing?.status || " "]?.variant || ""}
                              className={classNames(
                                "status-badge primary-para",
                                filing?.status
                                  ? `text-${TrackEFilingStatus[filing?.status]?.bgTextColor || ""}`
                                  : null
                              )}
                              pill={true}
                            >
                              {TrackEFilingStatus[filing?.status]?.label || "-"}
                            </Badge>
                            {filing?.efilingPlan?.efilingPlanTransaction?.orderStatus && (
                              <Badge
                                bg="success"
                                className="status-badge primary-para text-white"
                                pill={true}
                              >
                                {filing?.efilingPlan?.efilingPlanTransaction?.orderStatus}
                              </Badge>
                            )}
                          </div>
                        </td>
                      )}
                      {!hideColumns?.includes(FilingTableHeadings[1].title) && (
                        <td>{filing?.efilingCreatedBy?.email || "-"}</td>
                      )}
                      {!hideColumns?.includes(FilingTableHeadings[2].title) && (
                        <td>{filing?.efilingPlan?.planData?.title || "-"}</td>
                      )}
                      {!hideColumns?.includes(FilingTableHeadings[3].title) &&
                        (!filing?.efilingPlan?.inactive &&
                        filing?.efilingPlan?.planData &&
                        filing?.efilingPlan?.planData?.type !== "self" &&
                        !filing?.efilingAuthorizedUser ? (
                          <td>
                            <Badge bg="warning" className="primary-para text-white" pill={true}>
                              Awaiting a Tax Expert
                            </Badge>
                          </td>
                        ) : (
                          <td>{filing?.efilingAuthorizedUser?.User?.fullName || "-"}</td>
                        ))}
                      {!hideColumns?.includes(FilingTableHeadings[4].title) && (
                        <td>{filing?.assessmentYear || "-"}</td>
                      )}
                      {/* {!hideColumns?.includes(FilingTableHeadings[5].title) && (
                        <td
                          className={classNames(
                            "fw-bold text-capitalize",
                            filing?.efilingPlan?.efilingPlanTransaction?.orderStatus
                              ? "text-success"
                              : "text-warning"
                          )}
                        >
                          {filing?.efilingPlan?.efilingPlanTransaction?.orderStatus}
                        </td>
                      )}
                      {!hideColumns?.includes(FilingTableHeadings[6].title) && (
                        <td
                          className={classNames(
                            "fw-bold",
                            filing?.status &&
                              TrackEFilingStatus[filing?.status] &&
                              `text-${TrackEFilingStatus[filing?.status].variant}`
                          )}
                        >
                          {TrackEFilingStatus[filing?.status]?.label || "-"}
                        </td>
                      )} */}
                      {!hideColumns?.includes(FilingTableHeadings[5].title) && (
                        <td>{UtilService.getDateTimeDisplay(filing?.createdAt) || "-"}</td>
                      )}
                      {!hideColumns?.includes(FilingTableHeadings[6].title) && (
                        <td
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <RowActionOverlay
                            popoverEvents={[
                              {
                                icon: faEye,
                                title: "View",
                                action: () => {
                                  navigate(`/itr-filings/${filing.uuid}`);
                                },
                                actionRules: rulesSvc.updateAndSingleEFiling
                              }
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ) : (
                <NoRecordFound colspan={FilingTableHeadings.length} />
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {!noRecords && (
        <TablePagination
          pageCount={pageCount}
          pageOffset={forcePage}
          handleOnPageChange={handleOnPageChange}
        />
      )}
    </>
  );
};

export default ITRFilingsTable;
